import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { useLiveOpsSnapshot } from "@/lib/dashboards";
import { useDashboardLayout } from "@/lib/dashboardLayouts";
import { GridDashboard } from "@/components/dashboards/GridDashboard";
import { PulseTimeline } from "@/components/dashboards/PulseTimeline";
import { PulseAlertsRail } from "@/components/dashboards/PulseAlertsRail";
import { PulseMobile } from "@/components/dashboards/PulseMobile";
import { DashboardCustomizer } from "@/components/dashboards/DashboardCustomizer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAdaptiveInterval } from "@/lib/use-adaptive-interval";
import { RealtimeStatusChip } from "@/components/RealtimeStatusChip";
import { useAuth } from "@/lib/auth";
import { LIVE_OPS_TOPIC } from "@/lib/dashboards";

const PulseMap = lazy(() =>
  import("@/components/dashboards/PulseMap").then((m) => ({ default: m.PulseMap }))
);

export const Route = createFileRoute("/_authenticated/dashboards/pulse")({
  component: PulsePage,
  head: () => ({ meta: [{ title: "Live Pulse — MissionReady" }] }),
});

function PulsePage() {
  const isMobile = useIsMobile();
  const { currentOrg } = useAuth();
  const [refreshMs, setRefreshMs] = useState(15_000);
  const effectiveMs = useAdaptiveInterval(refreshMs);
  const { data, isLoading } = useLiveOpsSnapshot(effectiveMs);
  const { widgets } = useDashboardLayout("pulse");

  if (isMobile) {
    return <PulseMobile snapshot={data as any} />;
  }

  const renderers: Record<string, () => React.ReactNode> = {
    "pulse.alerts":   () => <PulseAlertsRail snapshot={data as any} />,
    "pulse.map":      () => (
      <Suspense fallback={<div className="h-[460px] rounded-md bg-muted/40 animate-pulse" />}>
        <PulseMap
          officers={([...(data?.liveOfficers ?? []), ...(data?.staleHeartbeats ?? [])]) as any}
          sites={(data?.sites ?? []) as any}
          sos={(data?.sosOpen ?? []) as any}
          breaches={(data?.breaches ?? []) as any}
          geofences={(data?.geofences ?? []) as any}
        />
      </Suspense>
    ),
    "pulse.timeline": () => <PulseTimeline />,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {isLoading ? "Loading…" : `Snapshot ${data ? new Date(data.capturedAt).toLocaleTimeString() : ""}`}
          </span>
          {currentOrg && <RealtimeStatusChip topic={LIVE_OPS_TOPIC(currentOrg.id)} />}
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(refreshMs)} onValueChange={(v) => setRefreshMs(Number(v))}>
            <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="5000">5s</SelectItem>
              <SelectItem value="15000">15s</SelectItem>
              <SelectItem value="30000">30s</SelectItem>
              <SelectItem value="60000">60s</SelectItem>
            </SelectContent>
          </Select>
          <DashboardCustomizer dashboardKey="pulse" />
        </div>
      </div>
      <GridDashboard
        dashboardKey="pulse"
        widgets={widgets}
        render={(id: string) => renderers[id]?.() ?? null}
      />
    </div>
  );
}
